import React, {useState, useEffect} from 'react'
import {useSearchParams, useNavigate, Link} from 'react-router-dom'
import {Auth} from 'aws-amplify'
import {useCognitoAuth, mapCognitoAttributesToUserAttributes} from '../modules/auth'
import {acceptInvitation} from '../modules/membership/api'
import {getLogger} from '../../shared/utils/logUtils'
import {toAbsoluteUrl} from '../../shared/utils'

const INVITATION_RETURN_KEY = 'ob_invitation_return'

type InvitationState = 'loading' | 'ready' | 'accepting' | 'success' | 'error'

export const InvitationPage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const {currentUser, setCurrentUser} = useCognitoAuth()
  const token = searchParams.get('token')

  const [state, setState] = useState<InvitationState>('loading')
  const [errorMessage, setErrorMessage] = useState('')

  useEffect(() => {
    document.body.classList.add('signInPage')
    return () => {
      document.body.classList.remove('signInPage')
    }
  }, [])

  useEffect(() => {
    if (!token) {
      setState('error')
      setErrorMessage('No invitation token provided. Please check the link in your email.')
      return
    }
    setState('ready')
  }, [token])

  const saveReturnUrl = () => {
    if (token) {
      sessionStorage.setItem(INVITATION_RETURN_KEY, `/invitation?token=${token}`)
    }
  }

  const handleAccept = async () => {
    if (!token) return
    setState('accepting')
    try {
      await acceptInvitation(token)
      sessionStorage.removeItem(INVITATION_RETURN_KEY)

      try {
        const cognitoUser = await Auth.currentAuthenticatedUser({bypassCache: true})
        const updatedAttrs = await mapCognitoAttributesToUserAttributes(cognitoUser)
        setCurrentUser(updatedAttrs)
      } catch (refreshErr) {
        getLogger().warn('Could not refresh user attributes after invitation acceptance:', refreshErr)
      }

      setState('success')
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to accept invitation'
      setErrorMessage(msg)
      setState('error')
      getLogger().error('Accept invitation failed:', err)
    }
  }

  const renderContent = () => {
    if (!currentUser) {
      return (
        <>
          <div className='signin-icon-wrap'>
            <svg xmlns='http://www.w3.org/2000/svg' width='48' height='48' viewBox='0 0 24 24' fill='none' stroke='#2196f3' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round'>
              <path d='M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z' />
              <polyline points='22,6 12,13 2,6' />
            </svg>
          </div>
          <h1 className='signin-title'>Subscription Invitation</h1>
          <p className='signin-muted' style={{textAlign: 'center'}}>
            You&apos;ve been invited to join an OmicsBox subscription.
            Sign in or create an account to accept.
          </p>
          <div className='signin-invitation-actions'>
            <Link
              to='/signin'
              className='signin-btn signin-btn-primary'
              style={{textDecoration: 'none'}}
              onClick={saveReturnUrl}
            >
              Sign In
            </Link>
            <Link
              to='/signin/registration'
              className='signin-btn signin-btn-secondary'
              style={{textDecoration: 'none'}}
              onClick={saveReturnUrl}
            >
              Create Account
            </Link>
          </div>
        </>
      )
    }

    return (
      <>
        {state === 'loading' && (
          <>
            <div className='signin-icon-wrap'>
              <div className='signin-spinner' />
            </div>
            <h1 className='signin-title'>Loading invitation...</h1>
          </>
        )}

        {state === 'ready' && (
          <>
            <div className='signin-icon-wrap'>
              <svg xmlns='http://www.w3.org/2000/svg' width='48' height='48' viewBox='0 0 24 24' fill='none' stroke='#2196f3' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round'>
                <path d='M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z' />
                <polyline points='22,6 12,13 2,6' />
              </svg>
            </div>
            <h1 className='signin-title'>Accept Invitation</h1>
            <p className='signin-muted' style={{textAlign: 'center'}}>
              You&apos;ve been invited to join an OmicsBox subscription.
              Click below to accept and get started.
            </p>
            <button className='signin-btn signin-btn-primary' onClick={handleAccept}>
              Accept Invitation
            </button>
          </>
        )}

        {state === 'accepting' && (
          <>
            <div className='signin-icon-wrap'>
              <div className='signin-spinner' />
            </div>
            <h1 className='signin-title'>Accepting invitation...</h1>
            <p className='signin-muted' style={{textAlign: 'center'}}>
              Please wait while we set up your access.
            </p>
          </>
        )}

        {state === 'success' && (
          <>
            <div className='signin-icon-wrap'>
              <svg xmlns='http://www.w3.org/2000/svg' width='48' height='48' viewBox='0 0 24 24' fill='none' stroke='#16a34a' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round'>
                <path d='M22 11.08V12a10 10 0 1 1-5.93-9.14' />
                <polyline points='22 4 12 14.01 9 11.01' />
              </svg>
            </div>
            <h1 className='signin-title'>Welcome!</h1>
            <p className='signin-muted' style={{textAlign: 'center'}}>
              You&apos;ve successfully joined the subscription. You can now use OmicsBox cloud features.
            </p>
            <button
              className='signin-btn signin-btn-primary'
              onClick={() => { window.location.href = '/dashboard' }}
            >
              Go to Dashboard
            </button>
          </>
        )}

        {state === 'error' && (
          <>
            <div className='signin-icon-wrap'>
              <svg xmlns='http://www.w3.org/2000/svg' width='48' height='48' viewBox='0 0 24 24' fill='none' stroke='#ef4444' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round'>
                <circle cx='12' cy='12' r='10' />
                <line x1='15' y1='9' x2='9' y2='15' />
                <line x1='9' y1='9' x2='15' y2='15' />
              </svg>
            </div>
            <h1 className='signin-title'>Invitation Error</h1>
            <p className='signin-muted' style={{textAlign: 'center'}}>{errorMessage}</p>
            <div className='signin-invitation-actions'>
              <button
                className='signin-btn signin-btn-secondary'
                onClick={() => navigate('/dashboard')}
              >
                Go to Dashboard
              </button>
              {token && (
                <button
                  className='signin-btn signin-btn-primary'
                  onClick={() => { setState('ready'); setErrorMessage('') }}
                >
                  Try Again
                </button>
              )}
            </div>
          </>
        )}
      </>
    )
  }

  return (
    <div className='signin-page'>
      <div className='signin-bg-pattern'>
        <img
          src={toAbsoluteUrl('/media/logos/ob_logo.svg')}
          alt=''
          aria-hidden='true'
          className='signin-bg-logo signin-bg-logo-tl'
        />
        <img
          src={toAbsoluteUrl('/media/logos/ob_logo.svg')}
          alt=''
          aria-hidden='true'
          className='signin-bg-logo signin-bg-logo-br'
        />
      </div>

      <div className='signin-content'>
        <div className='signin-logo'>
          <img
            alt='OmicsBox Web'
            src={toAbsoluteUrl('/media/logos/ob_web_logo.svg')}
            className='signin-logo-img'
          />
        </div>

        <div className='signin-card'>
          {renderContent()}
        </div>
      </div>

      <footer className='signin-footer'>
        <div className='signin-footer-content'>
          <span>© 2025 BioBam® — OmicsBox®</span>
          <span className='signin-footer-sep'>·</span>
          <a href='https://www.biobam.com/about/' target='_blank' rel='noreferrer'>About</a>
          <span className='signin-footer-sep'>·</span>
          <a href='https://www.biobam.com/contact-us/' target='_blank' rel='noreferrer'>Contact</a>
          <span className='signin-footer-sep'>·</span>
          <a href='https://www.biobam.com/blog/' target='_blank' rel='noreferrer'>Blog</a>
        </div>
      </footer>
    </div>
  )
}
